import { useCallback, useEffect, useRef, useState } from 'react';
import ErrorReporter from 'src/monitoring/errorReporter';
import type {
  RealtimeAckMessage,
  RealtimeTopicStatus,
} from '../types/realtime.types';
import type {
  RealtimeSubscriptionOptions,
  RealtimeSubscriptionOperation,
  RealtimeSubscriptions,
} from '../types/realtime-client.type';
import { isSocketOpen } from '../utils/socket-connection.util';
import { getAckPayload, getAckTopics } from '../utils/socket-message.util';
import {
  normalizeTopics,
  findSubscriptionAdapter,
  serializeSocketRequests,
} from '../utils/socket-subscription.util';

const SUBSCRIPTION_ACK_TIMEOUT_MS = 10_000;

/**
 * Owns desired topics and authorization, independently of transport reconnects.
 * @param options - Current socket and account token accessor.
 * @returns Topic state and stable lifecycle operations.
 */
export const useRealtimeSubscriptions = ({
  socketRef,
  getAccessTokenSilently,
  adapters,
}: RealtimeSubscriptionOptions): RealtimeSubscriptions => {
  const [topicStatuses, setTopicStatuses] = useState<
    Record<string, RealtimeTopicStatus>
  >({});
  const [topicErrors, setTopicErrors] = useState<
    Record<string, string | undefined>
  >({});
  const topicCountsRef = useRef<Map<string, number>>(new Map());
  const topicStatusesRef = useRef<Record<string, RealtimeTopicStatus>>({});
  // Updated synchronously on ACK so a following frame cannot race React effects.
  const authorizedTopicsRef = useRef<Set<string>>(new Set());
  const ackTimeoutsRef = useRef<Map<string, number>>(new Map());

  const clearAckTimeout = useCallback((topic: string) => {
    const timer = ackTimeoutsRef.current.get(topic);
    if (timer !== undefined) window.clearTimeout(timer);
    ackTimeoutsRef.current.delete(topic);
  }, []);
  const clearAckTimeouts = useCallback(() => {
    ackTimeoutsRef.current.forEach((timer) => window.clearTimeout(timer));
    ackTimeoutsRef.current.clear();
  }, []);
  useEffect(() => clearAckTimeouts, [clearAckTimeouts]);

  const startAckTimeout = useCallback(
    (topic: string) => {
      clearAckTimeout(topic);
      const timer = window.setTimeout(() => {
        if (ackTimeoutsRef.current.get(topic) !== timer) return;
        ackTimeoutsRef.current.delete(topic);
        if (!topicCountsRef.current.has(topic)) return;
        authorizedTopicsRef.current.delete(topic);
        setTopicStatuses((current) => ({ ...current, [topic]: 'unavailable' }));
        setTopicErrors((current) => ({
          ...current,
          [topic]: 'Realtime subscription acknowledgement timed out',
        }));
      }, SUBSCRIPTION_ACK_TIMEOUT_MS);
      ackTimeoutsRef.current.set(topic, timer);
    },
    [clearAckTimeout]
  );

  useEffect(() => {
    topicStatusesRef.current = topicStatuses;
  }, [topicStatuses]);

  const sendSocketMessage = useCallback(
    async (message: RealtimeSubscriptionOperation) => {
      const socket = socketRef.current;
      if (!socket || !isSocketOpen(socket)) {
        return;
      }

      // Include token loading and send failures in the pending subscription's
      // bound. Expiry does not authorize delivery or initiate a socket retry.
      if (message.type === 'subscribe') {
        message.topics
          .filter((topic) => topicCountsRef.current.has(topic))
          .forEach(startAckTimeout);
      }

      try {
        const token =
          message.type === 'subscribe' ? await getAccessTokenSilently() : '';
        if (socketRef.current !== socket || !isSocketOpen(socket)) {
          return;
        }
        const currentTopics = message.topics.filter((topic) =>
          message.type === 'subscribe'
            ? topicCountsRef.current.has(topic)
            : !topicCountsRef.current.has(topic)
        );
        serializeSocketRequests(
          adapters,
          message.type,
          currentTopics,
          token
        ).forEach((request) => socket.send(request));
      } catch (error) {
        ErrorReporter.sendException(error);
      }
    },
    [adapters, getAccessTokenSilently, socketRef, startAckTimeout]
  );

  const subscribe = useCallback(
    (topics: string[]) => {
      const normalizedTopics = normalizeTopics(topics).filter((topic) =>
        findSubscriptionAdapter(adapters, topic)?.isEnabled()
      );

      const newlyAddedTopics: string[] = [];
      normalizedTopics.forEach((topic) => {
        const currentCount = topicCountsRef.current.get(topic) ?? 0;
        topicCountsRef.current.set(topic, currentCount + 1);
        if (currentCount === 0) {
          newlyAddedTopics.push(topic);
        }
      });

      if (newlyAddedTopics.length > 0) {
        setTopicStatuses((currentStatuses) => {
          const nextStatuses = { ...currentStatuses };
          newlyAddedTopics.forEach((topic) => {
            nextStatuses[topic] = 'pending';
          });
          return nextStatuses;
        });
        setTopicErrors((currentErrors) => {
          const nextErrors = { ...currentErrors };
          newlyAddedTopics.forEach((topic) => {
            delete nextErrors[topic];
          });
          return nextErrors;
        });
        void sendSocketMessage({
          type: 'subscribe',
          topics: newlyAddedTopics,
        });
      }

      return () => {
        const removedTopics: string[] = [];
        normalizedTopics.forEach((topic) => {
          const currentCount = topicCountsRef.current.get(topic) ?? 0;
          if (currentCount <= 1) {
            clearAckTimeout(topic);
            topicCountsRef.current.delete(topic);
            authorizedTopicsRef.current.delete(topic);
            removedTopics.push(topic);
            return;
          }

          topicCountsRef.current.set(topic, currentCount - 1);
        });

        if (removedTopics.length > 0) {
          setTopicStatuses((currentStatuses) => {
            const nextStatuses = { ...currentStatuses };
            removedTopics.forEach((topic) => {
              delete nextStatuses[topic];
            });
            return nextStatuses;
          });
          setTopicErrors((currentErrors) => {
            const nextErrors = { ...currentErrors };
            removedTopics.forEach((topic) => {
              delete nextErrors[topic];
            });
            return nextErrors;
          });
          void sendSocketMessage({
            type: 'unsubscribe',
            topics: removedTopics,
          });
        }
      };
    },
    [adapters, clearAckTimeout, sendSocketMessage]
  );

  const resubscribeCurrentTopics = useCallback(() => {
    const topics = Array.from(topicCountsRef.current.keys());
    if (topics.length === 0) {
      return;
    }

    setTopicStatuses((currentStatuses) => {
      const nextStatuses = { ...currentStatuses };
      topics.forEach((topic) => {
        nextStatuses[topic] = 'pending';
      });
      return nextStatuses;
    });
    setTopicErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      topics.forEach((topic) => {
        delete nextErrors[topic];
      });
      return nextErrors;
    });
    void sendSocketMessage({ type: 'subscribe', topics });
  }, [sendSocketMessage]);

  const handleAckMessage = useCallback(
    (message: RealtimeAckMessage) => {
      const payload = getAckPayload(message.payload);
      const operations = new Map(
        adapters.map((adapter) => [
          adapter,
          adapter.getAcknowledgedOperation(message),
        ])
      );
      const acknowledgements = getAckTopics(
        payload,
        topicStatusesRef.current
      ).flatMap((topic) => {
        const adapter = findSubscriptionAdapter(adapters, topic);
        if (!adapter) return [];
        const operation = operations.get(adapter);
        if (!operation) return [];
        const subscribing = operation === 'subscribe';
        if (subscribing !== topicCountsRef.current.has(topic)) return [];
        return [{ topic, adapter, operation }];
      });
      if (acknowledgements.length === 0) return;
      const topics = acknowledgements.map(({ topic }) => topic);
      topics.forEach(clearAckTimeout);
      acknowledgements.forEach(({ topic, adapter, operation }) => {
        if (!adapter.requiresAuthorization) return;
        if (operation === 'subscribe' && message.type === 'ack') {
          authorizedTopicsRef.current.add(topic);
        } else {
          authorizedTopicsRef.current.delete(topic);
        }
      });

      setTopicStatuses((currentStatuses) => {
        const nextStatuses = { ...currentStatuses };
        acknowledgements.forEach(({ topic, operation }) => {
          if (operation === 'unsubscribe') {
            delete nextStatuses[topic];
            return;
          }

          nextStatuses[topic] =
            message.type === 'error' ? 'error' : 'subscribed';
        });
        return nextStatuses;
      });

      setTopicErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        acknowledgements.forEach(({ topic, operation }) => {
          if (operation === 'unsubscribe' || message.type !== 'error') {
            delete nextErrors[topic];
            return;
          }

          nextErrors[topic] =
            typeof payload.message === 'string'
              ? payload.message
              : 'Unable to subscribe to realtime topic';
        });
        return nextErrors;
      });
    },
    [adapters, clearAckTimeout]
  );
  const resetAuthorization = useCallback(() => {
    clearAckTimeouts();
    authorizedTopicsRef.current.clear();
  }, [clearAckTimeouts]);
  const canDeliverEvent = useCallback(
    (topic: string) => {
      const adapter = findSubscriptionAdapter(adapters, topic);
      return (
        topicCountsRef.current.has(topic) &&
        Boolean(adapter?.isEnabled()) &&
        (!adapter?.requiresAuthorization ||
          authorizedTopicsRef.current.has(topic))
      );
    },
    [adapters]
  );
  return {
    topicStatuses,
    topicErrors,
    subscribe,
    handleAckMessage,
    canDeliverEvent,
    resubscribeCurrentTopics,
    resetAuthorization,
  };
};
